import React, { useState } from "react";
import { Box, Typography, Menu, MenuItem, Chip, Tooltip, CircularProgress } from "@mui/material";
import { KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius, shadows } from "../../constants";
import { useAuth } from "../../context/AuthContext";
import { findPost, formatPostShortName } from "../../services/postSelection";
import { authService } from "../../services/authService";

/**
 * PostSelector
 * Header dropdown for switching which "post" (e.g. GM for a given region)
 * is currently active, when a user holds more than one. Renders nothing
 * for single-post/no-post users, so it's safe to mount unconditionally.
 */
const PostSelector = () => {
  const { posts, activePostId, setActivePost } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingPostId, setSwitchingPostId] = useState(null);

  // No dropdown needed when there's nothing to switch between.
  if (!posts || posts.length < 2) {
    return null;
  }

  const activePost = findPost({ posts }, activePostId) || posts[0];
  // Prefer the short name in the compact header chip (matches the value the
  // backend now filters queries by — see postShortName on Query/User.posts);
  // the full postName still shows per-option in the dropdown below.
  const label = formatPostShortName(activePost.postShortName) || activePost.postName || activePost.postCode || "Post";

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (postId) => {
    handleClose();
    if (postId === activePostId || !setActivePost(postId)) {
      return;
    }

    setSwitchingPostId(postId);
    try {
      // AccessRights are scoped per-post, but they're only ever baked into
      // the SSO login JWT for whichever post was active at login time —
      // refresh them for the newly selected post before reloading, so the
      // reloaded page reflects the correct permissions, not stale ones.
      const response = await authService.getAccessRights(postId);
      const accessRights = response?.data?.accessRights;
      if (accessRights) {
        localStorage.setItem("AccessRights", JSON.stringify(accessRights));
      }
    } catch (error) {
      // Non-blocking: fall back to whatever AccessRights are already
      // cached rather than stopping the user from switching posts.
      // eslint-disable-next-line no-console
      console.error("Failed to refresh AccessRights for post switch:", error);
    } finally {
      // Every page's data-fetching is scoped by the X-Post-Id header at
      // request time (see services/api.js), but nothing currently mounted
      // will spontaneously refetch just because activePostId changed in
      // context. A full reload is the simplest way to guarantee every
      // query/project list — and every AccessRights-gated control — on the
      // current page re-reads under the newly selected post.
      window.location.reload();
    }
  };

  return (
    <>
      <Tooltip title="Switch post" arrow placement="bottom">
        <Box
          role="button"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          aria-disabled={Boolean(switchingPostId)}
          onClick={switchingPostId ? undefined : handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: spacing.xs,
            cursor: switchingPostId ? "default" : "pointer",
            opacity: switchingPostId ? 0.6 : 1,
            px: spacing.sm,
            py: "4px",
            borderRadius: borderRadius.sm,
            border: `1px solid ${colors.borderDefault}`,
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: switchingPostId ? "transparent" : colors.backgroundSoft
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: fonts.weightSemiBold,
              fontSize: fonts.fontSizeSm,
              color: colors.textPrimaryDark,
              whiteSpace: "nowrap"
            }}
          >
            {label}
          </Typography>
          {switchingPostId ? (
            <CircularProgress size={14} sx={{ color: colors.textSecondary }} />
          ) : (
            <ArrowDownIcon
              sx={{
                color: colors.textSecondary,
                fontSize: "16px",
                transition: "transform 0.15s ease",
                transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)"
              }}
            />
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: borderRadius.md,
            minWidth: "260px",
            maxWidth: "320px",
            marginTop: spacing.xs,
            boxShadow: shadows.lg,
            border: `1px solid ${colors.borderDefault}`,
            backgroundColor: colors.backgroundWhite
          }
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        {posts.map((post) => {
          const isActive = post.postId === activePostId;
          const projectCount = post.projects?.length || 0;

          return (
            <MenuItem
              key={post.postId}
              selected={isActive}
              onClick={() => handleSelect(post.postId)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "2px",
                padding: `${spacing.xs} ${spacing.sm}`,
                minHeight: "auto",
                "&:hover": {
                  backgroundColor: colors.backgroundSoft
                },
                "&.Mui-selected": {
                  backgroundColor: colors.backgroundSoft
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: fonts.fontSizeSm,
                  fontWeight: fonts.weightSemiBold,
                  color: colors.textPrimaryDark
                }}
              >
                {post.postName || post.postCode || post.postId}
                {post.postShortName ? ` (${formatPostShortName(post.postShortName)})` : ""}
              </Typography>
              <Chip
                label={projectCount === 0 ? "No projects" : `${projectCount} project${projectCount === 1 ? "" : "s"}`}
                size="small"
                sx={{
                  mt: "2px",
                  height: "20px",
                  fontSize: fonts.fontSizeXs,
                  fontWeight: fonts.weightSemiBold,
                  backgroundColor: `${colors.primary}15`,
                  color: colors.primary
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default PostSelector;
